import { COMPONENT_TOKEN_CSS_VARS, ComponentToken, ComponentTokenOverrides, CUSTOMER_COMPONENT_TOKENS } from '~constants/component_tokens';
import { DEFAULT_DARK_THEME, DEFAULT_LIGHT_THEME, THEME_TOKEN_CSS_VARS, themeColourCssVar } from '~constants/theming';
import { Branding } from '~schemas/branding';
import { Theming } from '~schemas/settings';

/**
 * Merge the /v2/branding API response onto the hardcoded defaults.
 *
 * Every branding field is nullable, so each one falls back to the default theme
 * individually. A tenant with no branding gets the plain default theme.
 *
 * Mapping:
 *   colours.brand.primary  → surface.focus
 *   colours.brand.surface  → surface.primary
 *   colours.text.heading   → text.heading
 *   colours.text.body      → text.body
 *   fonts.heading          → fonts.heading
 *   fonts.body             → fonts.body
 */
export function buildThemingFromBranding(branding: Branding): Theming {
  const defaultTheme = branding.dark_mode ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME;
  const { colours, fonts } = branding;

  return {
    ...defaultTheme,
    fonts: {
      heading: fonts?.heading ?? defaultTheme.fonts.heading,
      body: fonts?.body ?? defaultTheme.fonts.body,
    },
    surface: {
      ...defaultTheme.surface,
      focus: colours?.brand?.primary ?? defaultTheme.surface.focus,
      primary: colours?.brand?.surface ?? defaultTheme.surface.primary,
    },
    text: {
      ...defaultTheme.text,
      heading: colours?.text?.heading ?? defaultTheme.text.heading,
      body: colours?.text?.body ?? defaultTheme.text.body,
    },
  };
}

export function generateFontUrls(theme: Theming): string[] {
  const urls: string[] = [];

  Object.values(theme.fonts).forEach((v) => {
    if (v.url && v.url.startsWith('https://')) {
      urls.push(v.url);
      return;
    }

    // A blank family would request `family=` and get a 400 back, so there is
    // nothing to load and the default typeface applies instead.
    const family = v.family?.trim();
    if (!family) {
      return;
    }

    // Google fonts
    const fontWeights = ['400', '500', '600', '700', '800'];
    const fontUrl = `https://fonts.googleapis.com/css?family=${family}:${fontWeights.join(',')}`;
    urls.push(fontUrl);
  });

  return urls;
}

export function applyFont(url: string) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);
}

function applyMapping(mapping: Record<string, string>, values: Record<string, unknown>) {
  Object.entries(mapping).forEach(([key, cssVar]) => {
    const mappedValue = values[key];
    if (mappedValue) {
      const value =
        typeof mappedValue === 'object' && 'family' in mappedValue && typeof mappedValue.family === 'string'
          ? mappedValue.family
          : mappedValue;

      if (typeof value !== 'string') return;
      document.documentElement.style.setProperty(cssVar, value);
    }
  });
}

export function applyTheming(theming: Theming) {
  const mappings: Record<keyof Theming, Record<string, string>> = THEME_TOKEN_CSS_VARS;

  // Fix: Explicitly assert that section is a keyof Theming
  (Object.keys(mappings) as (keyof Theming)[]).forEach((section) => {
    applyMapping(mappings[section], theming[section] || {});
  });

  // Apply fonts
  generateFontUrls(theming).forEach((url) => {
    applyFont(url);
  });
}

/**
 * Look up the component token overrides for a customer.
 *
 * `customerId` is null when the action token is missing, malformed, or carries no
 * `customer_id` claim, which resolves to no overrides and therefore the defaults
 * from `index.css`. That is the right outcome — theming must never block the
 * dashboard — but it is indistinguishable from "this customer has no override", so
 * it warns in development to keep a bad token from reading as a styling mistake.
 */
export function resolveComponentTokens(customerId: number | null): ComponentTokenOverrides {
  if (customerId === null) {
    if (import.meta.env.DEV && Object.keys(CUSTOMER_COMPONENT_TOKENS).length > 0) {
      console.warn('No customer_id on the action token: component token overrides cannot be resolved, using defaults.');
    }
    return {};
  }

  return CUSTOMER_COMPONENT_TOKENS[customerId]?.tokens ?? {};
}

/**
 * Write component token overrides onto <html>.
 *
 * A `themeToken` override is written as a `var()` reference rather than a resolved
 * colour, so the token keeps following branding when `applyTheming` later updates
 * the theme token it points at — the two can be applied in either order.
 */
export function applyComponentTokens(overrides: ComponentTokenOverrides) {
  (Object.keys(overrides) as ComponentToken[]).forEach((token) => {
    const override = overrides[token];
    if (!override) return;

    const value = 'colour' in override ? override.colour.trim() : `var(${themeColourCssVar(override.themeToken)})`;

    // A blank colour would clear the property and leave the element with no
    // background at all, so leave the default in place instead.
    if (!value) return;

    document.documentElement.style.setProperty(COMPONENT_TOKEN_CSS_VARS[token], value);
  });
}
